import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../components/Button';
import { faqCategoryId } from '../utils/faqCategoryId';
import '../styles/About.css';

const ABOUT_QUOTES = [
  {
    text: 'The glory of a good lawyer is to win a bad trial.',
    attribution: 'Balzac',
  },
  {
    text: 'The life of the law has not been logic; it has been experience.',
    attribution: 'Oliver Wendell Holmes Jr.',
  },
  {
    text: 'The good of the people is the highest law.',
    attribution: 'Cicero',
  },
  {
    text: 'Injustice anywhere is a threat to justice everywhere.',
    attribution: 'Martin Luther King Jr.',
  },
  {
    text: 'Justice delayed is justice denied.',
    attribution: 'William E. Gladstone',
  },
];

function About() {
  const navigate = useNavigate();
  const [flippedCards, setFlippedCards] = useState({});
  const [quote] = useState(
    () => ABOUT_QUOTES[Math.floor(Math.random() * ABOUT_QUOTES.length)]
  );

  const toggleCard = (id) => {
    setFlippedCards((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="about-page">
      <section className="about-hero">
        <div className="about-hero-content">
          <h1>Jones & Booth</h1>
          <p className="about-founded">Founded in 2017</p>
          <p>Jones & Booth was founded with a shared vision: to create a law firm where clients feel heard, supported, and fiercely advocated for. Our founding partners first met in law school and later reunited after building successful individual practices. They came together with a common belief that clients deserve a more thoughtful, personalized approach to legal representation.</p>
        </div>
      </section>

      <section className="about-content">
        <div className="about-container">
          <div className="about-our-lawyers">
            <h3>Our Team</h3>
            <p className="about-lawyers-intro">Meet the attorneys and client advocates who serve Jones & Booth.</p>
            <div className="lawyers-grid">
              <div
                className={`lawyer-card ${flippedCards.matt ? 'flipped' : ''}`}
                onClick={() => toggleCard('matt')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleCard('matt');
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label={flippedCards.matt ? 'Show Matthew L. Jones summary' : 'Show Matthew L. Jones full profile'}
              >
                <div className="card-inner">
                  <div className="card-front">
                    <div className="lawyer-headshot">
                      <img src="/jblassets/mlj.png" alt="Matthew L. Jones" />
                    </div>
                    <div className="lawyer-info">
                      <h4>Matthew L. Jones</h4>
                      <p className="lawyer-title">Personal Injury, Estates, and Business Law</p>
                      <p className="lawyer-bio">Matthew Jones became an attorney because he wanted to help people and their families get through difficult times. Matt's primary areas of focus are personal injury, estate planning, and probate matters. He values the opportunity to help people who are hurting and is a compassionate advocate in their time of need.</p>
                      <div className="lawyer-cta" onClick={(e) => e.stopPropagation()}>
                        <Button variant="primary" onClick={() => navigate('/qualify')}>
                          Contact Us
                        </Button>
                      </div>
                      <p className="card-flip-hint">Click for education, experience & affiliations</p>
                    </div>
                  </div>
                  <div className="card-back">
                    <div className="lawyer-details">
                      <h4>Matthew L. Jones</h4>
                      <div className="lawyer-details-grid">
                        <div className="detail-section">
                          <h5>Education</h5>
                          <p>Juris Doctor, Mercer Law School (top 15%, Vale Moot Court Team, President of Student Trial Lawyers Association, Editor of Mercer Law Free Press)<br />Bachelor of Arts in History and Spanish, University of Georgia</p>
                        </div>
                        <div className="detail-section">
                          <h5>Experience</h5>
                          <p>Matt has experience seeing cases through, from start to finish, at large, medium, and small law firms around the State of Georgia. Skilled in successful strategies for addressing his clients' specific needs, he helps them navigate the difficult legal process and emerge with an optimistic outlook on the future.</p>
                        </div>
                        <div className="detail-section detail-section-wide">
                          <h5>Professional Affiliations</h5>
                          <p>Georgia Trial Lawyers Association, Atlanta Bar Association, Cobb County Bar Association, Young Lawyers Division of the State Bar of Georgia. Graduate of the Young Lawyers Leadership Academy. Former Co-Chair, YLD Committee of Legal Ethics and Professionalism (two terms).</p>
                        </div>
                      </div>
                      <div className="lawyer-cta lawyer-cta-back" onClick={(e) => e.stopPropagation()}>
                        <Button variant="primary" onClick={() => navigate('/qualify')}>
                          Contact Us
                        </Button>
                      </div>
                      <p className="card-flip-hint">Click to return to summary</p>
                    </div>
                  </div>
                </div>
              </div>

              <div
                className={`lawyer-card ${flippedCards.walter ? 'flipped' : ''}`}
                onClick={() => toggleCard('walter')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleCard('walter');
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label={flippedCards.walter ? 'Show Walter L. Booth summary' : 'Show Walter L. Booth full profile'}
              >
                <div className="card-inner">
                  <div className="card-front">
                    <div className="lawyer-headshot">
                      <img src="/jblassets/walterlboothjr.jpg" alt="Walter L. Booth" />
                    </div>
                    <div className="lawyer-info">
                      <h4>Walter L. Booth</h4>
                      <p className="lawyer-title">Personal Injury, Real Estate Law, and Business Law</p>
                      <p className="lawyer-bio">
                        Walter is an experienced attorney with strong business acumen. Before co-founding JB, Walter practiced with a large law firm in Atlanta where he honed his legal skills. Walter has significant experience in banking, lending, business, construction, real estate, and landlord/tenant matters. Prior to practicing law, Walter was a successful banker with a large financial institution, where he advised high-net-worth clients and businesses.
                      </p>
                      <div className="lawyer-cta" onClick={(e) => e.stopPropagation()}>
                        <Button variant="primary" onClick={() => navigate('/qualify')}>
                          Contact Us
                        </Button>
                      </div>
                      <p className="card-flip-hint">Click for education, experience & affiliations</p>
                    </div>
                  </div>
                  <div className="card-back">
                    <div className="lawyer-details">
                      <h4>Walter L. Booth</h4>
                      <div className="lawyer-details-grid">
                        <div className="detail-section">
                          <h5>Education</h5>
                          <p>
                            Juris Doctor, Mercer Law School (certificate in corporate finance, Mercer University Stetson School of Business)
                            <br />
                            Bachelor of Arts in Health Science, Furman University
                          </p>
                        </div>
                        <div className="detail-section">
                          <h5>Experience</h5>
                          <p>
                            Prior to his banking career, Walter enjoyed a brief professional athletic career abroad, and returned stateside to train and advise an extensive list of professional athletes. Walter now advises professional athletes on preparing for their transition out of sports and into the business world. Walter also owns a franchise business in Duluth, Georgia.
                          </p>
                        </div>
                        <div className="detail-section detail-section-wide">
                          <h5>Professional Affiliations</h5>
                          <p>
                            At Furman, Walter was a decorated athlete and voted football team captain. At Mercer Law, Walter was a member of student government, the Sports and Entertainment Law Society, the Mock Trial Team, Mercer Advocacy Council, BLSA, SRBLSA, and Phi Delta Phi. Walter is a member of Kappa Alpha Psi Fraternity, Inc.
                          </p>
                        </div>
                      </div>
                      <div className="lawyer-cta lawyer-cta-back" onClick={(e) => e.stopPropagation()}>
                        <Button variant="primary" onClick={() => navigate('/qualify')}>
                          Contact Us
                        </Button>
                      </div>
                      <p className="card-flip-hint">Click to return to summary</p>
                    </div>
                  </div>
                </div>
              </div>

              <div
                className={`lawyer-card ${flippedCards.peter ? 'flipped' : ''}`}
                onClick={() => toggleCard('peter')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleCard('peter');
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label={flippedCards.peter ? 'Show Peter Jones summary' : 'Show Peter Jones full profile'}
              >
                <div className="card-inner">
                  <div className="card-front">
                    <div className="lawyer-headshot">
                      <img src="/jblassets/peterheadshotbackground.png" alt="Peter Jones" />
                    </div>
                    <div className="lawyer-info">
                      <h4>Peter Jones</h4>
                      <p className="lawyer-title">Intake Coordinator and Legal Assistant</p>
                      <p className="lawyer-bio">
                        Peter Jones serves as Intake Coordinator and Legal Assistant at Jones & Booth. He plays a central role in client communication and case administration. From the earliest stages of a matter, he works closely with current and prospective clients. He facilitates scheduling and ensures that inquiries receive timely and professional attention. In addition to client contact, Peter indexes medical records, negotiates medical bill reductions, supervises service of process, organizes firm files, and serves as a liaison with the courts.
                      </p>
                      <div className="lawyer-cta" onClick={(e) => e.stopPropagation()}>
                        <Button variant="primary" onClick={() => navigate('/qualify')}>
                          Contact Us
                        </Button>
                      </div>
                      <p className="card-flip-hint">Click for role details & how Peter supports your case</p>
                    </div>
                  </div>
                  <div className="card-back">
                    <div className="lawyer-details">
                      <h4>Peter Jones</h4>
                      <div className="lawyer-details-grid">
                        <div className="detail-section">
                          <h5>Client Intake & Scheduling</h5>
                          <p>
                            Peter communicates with current and prospective clients. He addresses preliminary inquiries and coordinates scheduling so that matters proceed efficiently from first contact. He also ensures that clients understand next steps and that appointments and follow-up are arranged with care and accuracy.
                          </p>
                        </div>
                        <div className="detail-section">
                          <h5>Records, Billing & Service</h5>
                          <p>
                            He indexes medical records for use in case development. He negotiates medical bill reductions on behalf of clients and supervises service of process. This work helps keep case materials organized, medical costs under control, and procedural requirements satisfied as matters advance.
                          </p>
                        </div>
                        <div className="detail-section detail-section-wide">
                          <h5>Firm Files & Court Liaison</h5>
                          <p>
                            Peter maintains firm files and serves as a liaison with the courts to support deadlines, filings, and case administration. By keeping firm-side materials in order and coordinating with court offices as needed, he helps the attorneys focus on advocacy. He also ensures that clients receive consistent and informed communication.
                          </p>
                        </div>
                      </div>
                      <div className="lawyer-cta lawyer-cta-back" onClick={(e) => e.stopPropagation()}>
                        <Button variant="primary" onClick={() => navigate('/qualify')}>
                          Contact Us
                        </Button>
                      </div>
                      <p className="card-flip-hint">Click to return to summary</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="services">
        <h3>What We Do</h3>
        <div className="services-grid">
          <Link
            to={`/faq#${faqCategoryId('Motor Vehicle')}`}
            className="service-item"
          >
            <h4>Motor Vehicle Accidents</h4>
            <p>Car, truck, motorcycle, and rideshare accidents. We fight for fair compensation.</p>
          </Link>
          <Link
            to={`/faq#${faqCategoryId('Premises Liability')}`}
            className="service-item"
          >
            <h4>Premises Liability</h4>
            <p>Slip and fall, negligent security, inadequate maintenance, and more.</p>
          </Link>
          <Link
            to={`/faq#${faqCategoryId('Catastrophic Injury')}`}
            className="service-item"
          >
            <h4>Catastrophic & Workplace Injury</h4>
            <p>Traumatic brain injury, wrongful death, construction accidents, and industrial injuries.</p>
          </Link>
          <Link
            to={`/faq#${faqCategoryId('Estate & Probate')}`}
            className="service-item"
          >
            <h4>Estate Planning & Probate</h4>
            <p>Wills, trusts, probate litigation, and contested estate matters.</p>
          </Link>
          <Link
            to={`/faq#${faqCategoryId('Real Estate Law')}`}
            className="service-item"
          >
            <h4>Real Estate Law</h4>
            <p>Commercial and residential lease disputes, quiet title, partition actions.</p>
          </Link>
        </div>
      </section>

      <section className="lawyers-marketing">
        <div className="marketing-content">
          <h4>Our Commitment</h4>
          <p>By combining experience in and out of the courtroom with careful preparation, creative thinking, and modern legal tools, our attorneys develop strategies designed to achieve meaningful results. We take pride not only in the outcomes we secure, but in the trust we build with the people we represent. At Jones & Booth, we are always looking for ways to improve our practice and better serve our clients—because there is always a better way forward.</p>

          <blockquote className="about-quote">
            {quote.text} — {quote.attribution}
          </blockquote>
        </div>
      </section>

      <section className="contact-cta">
        <div className="contact-cta-content">
          <h2>Ready to Contact Us?</h2>
          <p>Contact our experienced legal team for a consultation. We're here to help you achieve the justice and peace of mind you deserve.</p>
          <div className="cta-buttons">
            <Button variant="primary" onClick={() => navigate('/qualify')}>
              Contact Us Today
            </Button>
            <Button variant="secondary" onClick={() => navigate('/blog')}>
              Read Our Blog
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

export default About;
